import React, { useState, useEffect } from "react";
import { LinearGradient } from "react-text-gradients";
import SideBar from "../../Common/SideBar";
import { useDispatch, useSelector } from "react-redux";
import CouponForm from "./Components/CouponForm";
import CouponTable from "./Components/CouponTable";
import "./styles/CouponScreen.css";
import { 
  getAllCoupons, 
  updateCouponStatus, 
  updateCoupon, 
  createCoupon 
} from "../../../services/couponfirebaseConnect";
import { successToast, errorToast } from "../../../services/toastService";

export default function CouponScreen() {
  const sidebar = useSelector(state => state.sidebar);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCoupons();
  }, []);

  const fetchCoupons = async () => {
    try {
      setLoading(true);
      const fetchedCoupons = await getAllCoupons();
      console.log('Fetched coupons:', fetchedCoupons); // Debug log
      const mappedCoupons = fetchedCoupons.map(coupon => ({
        id: coupon.id,
        name: coupon.name || '',
        description: coupon.description || '',
        minProjectValue: coupon.minProjectValue || '',
        offValidation: {
          condition: coupon.offValidation?.condition || 'PERCENTAGE',
          value: coupon.offValidation?.value || ''
        },
        status: coupon.status || 'ACTIVE',
        type: coupon.type || 'PROJECT',
        validity: {
          from: coupon.validity?.from || new Date(),
          to: coupon.validity?.to || new Date()
        }
      }));
      console.log('Mapped coupons:', mappedCoupons); // Debug log
      setCoupons(mappedCoupons);
    } catch (error) {
      console.error('Error in fetchCoupons:', error); // Debug log
      errorToast(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddCoupon = () => {
    setEditingCoupon(null);
    setIsFormOpen(true);
  };

  const handleEditCoupon = (coupon) => {
    console.log('Editing coupon:', coupon); // Debug log
    if (!coupon) {
      console.error('No coupon data provided to handleEditCoupon');
      return;
    }
    const editData = {
      id: coupon.id,
      name: coupon.name || '',
      description: coupon.description || '',
      minProjectValue: coupon.minProjectValue || '',
      offValidation: {
        condition: coupon.offValidation?.condition || 'PERCENTAGE',
        value: coupon.offValidation?.value || ''
      },
      status: coupon.status || 'ACTIVE',
      type: coupon.type || 'PROJECT',
      validity: {
        from: coupon.validity?.from?.toDate?.() || new Date(),
        to: coupon.validity?.to?.toDate?.() || new Date()
      }
    };
    console.log('Setting editingCoupon:', editData); // Debug log
    setEditingCoupon(editData);
    setIsFormOpen(true);
  };

  const handleCouponSubmit = async (formData) => {
    console.log('Submitting coupon:', formData); // Debug log
    console.log('Current editingCoupon:', editingCoupon); // Debug log
    try {
      if (!formData) {
        throw new Error('No form data provided');
      }

      const submissionData = {
        name: formData.name,
        description: formData.description,
        minProjectValue: formData.minProjectValue,
        offValidation: {
          condition: formData.offValidation?.condition || 'PERCENTAGE',
          value: formData.offValidation?.value || ''
        },
        status: formData.status,
        type: formData.type,
        validity: {
          from: formData.validity.from,
          to: formData.validity.to
        }
      };

      console.log('Prepared submission data:', submissionData); // Debug log

      if (editingCoupon?.id) {
        console.log('Updating coupon with ID:', editingCoupon.id); // Debug log
        const result = await updateCoupon(editingCoupon.id, submissionData);
        console.log('Update result:', result); // Debug log
      } else {
        console.log('Creating new coupon'); // Debug log
        const result = await createCoupon(submissionData);
        console.log('Create result:', result); // Debug log
      }

      // Close form first
      setIsFormOpen(false);
      setEditingCoupon(null);

      // Then refresh data
      await fetchCoupons();
      
      // Show success message last
      successToast("Coupon operation successful");
    } catch (error) {
      console.error('Error in handleCouponSubmit:', error); // Debug log
      errorToast(error.message);
    }
  };

  const handleStatusChange = async (couponId, newStatus) => {
    try {
      await updateCouponStatus(couponId, newStatus);
      await fetchCoupons();
      successToast("Status updated successfully");
    } catch (error) {
      errorToast(error.message);
    }
  };

  return (
    <div className="MainContainer">
      <SideBar />
      <div
        style={{ width: sidebar.expand ? "84vw" : "95vw" }}
        className="ScreenContainer"
      >
        {/* page heading and profile section */}
        <div className="pageHeadingContainerCoupon" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          <div className="pageHeadingInnerContainer">
            <div className="pageHeading">
              Coupons
            </div>
          </div>
          <div style={{ display: 'flex', gap: '1rem' }}>
            <div className="upperBarSearchBox upperBarSearchBoxNotification">
              <img
                src={require("../../../assets/exit.png")}
                alt="notification-bell"
              />
            </div>
            <div
              className="upperBarSearchBox upperBarSearchBoxProfile"
              style={{ border: "none" }}
            >
              <img
                src={require("../../../assets/profile.png")}
                alt="profile-pic"
              />
            </div>
          </div>
        </div>

        {/* search bar */}
        <div className="upperSearchBarContainer" style={{ width: '100%', marginTop: '1rem' }}>
          <div className="upperBarSearchBar" style={{ width: '100%' }}>
            <input placeholder="Search..." className="upperBarSearchBarInput" style={{ width: '100%' }} />
          </div>
        </div>

        {/* Add Coupon Button */}
        <div className="addCouponButtonContainer">
          <button className="addCouponButton" onClick={handleAddCoupon}>
            Add Coupon
          </button>
        </div>

        {/* Coupon Table */}
        <CouponTable 
          coupons={coupons} 
          onEditCoupon={handleEditCoupon} 
        />

        {/* Coupon Form Modal */}
        <CouponForm
          isOpen={isFormOpen}
          onClose={() => setIsFormOpen(false)}
          onSubmit={handleCouponSubmit}
          initialData={editingCoupon}
        />
      </div>
    </div>
  );
}
